import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { HeroId } from '../game/data/heroes';
import type { Card, TribeId } from '../game/data/cards/types';
import { cardsByTier } from '../game/data/cards';
import { rollShop, newInstance } from '../game/shop';
import { generateOpponentBoard } from '../game/ai';
import {
  MAX_GOLD,
  BASE_GOLD_PER_TURN,
  TAVERN_UPGRADE_COSTS,
} from '../game/data/gameConfig';
import { allHeroes } from '../game/data/heroes';
import {
  gameState as v1GameState,
  initNewLobby,
  type PlayerState,
} from '../game/gameState';
import {
  simulateCombat,
  type CombatResult,
} from '../game/combatEngine';
import { generatePairings, type Pairing } from '../game/pairing';
import {
  createLobby,
  runRoundTick,
  type AIPlayer,
  type PairingOutcome,
} from '../game/lobby';
import { mulberry32, freshSeed } from '../game/rng';
import { applyTribeEndOfTurn } from '../game/tribeEndOfTurn';
import {
  findTripleIndices,
  createGoldenFromTriple,
  getTripleRewardTier,
} from '../game/triples';
import {
  fireTavernTrigger,
  occupiedSlots,
  clearCombatBuffs,
  HAND_LIMIT,
  type PendingDiscover,
  type TavernTriggerFacts,
} from '../game/tavernAbilities';
import {
  applyOnBuyHeroPower,
  applyEndOfTurnHeroPowers,
  applyPostCombatHeroPowers,
  applyStartOfTurnHeroPowers,
  refreshCostForHero,
  applyOnRefreshHeroPower,
  tierUpCostForHero,
  grantsUnitOnTierUp,
} from '../game/heroPowers';
import {
  diffTavernFx,
  goldGainFx,
  type TavernFxEvent,
} from '../game/tavernFx';
import {
  classifyPower,
  applyActivePower,
  applyTargetedPower,
  getHeroById,
} from '../game/heroPowerAdapter';
import {
  publishSnapshot,
  fetchOpponentSnapshot,
  recordMatchResult,
  simulateCombatRemote,
  type OpponentSnapshot,
} from '../net/asyncClient';
import { useAccountStore } from './account';

// Sentinel id used as the human's opponent in pairings when an async
// snapshot is fetched. runRoundTick won't find this in `opponents`, so
// human-win damage isn't credited to any real AI. Exported for tests.
export const ASYNC_OPPONENT_ID = 'P-ASYNC';

// Ring buffer of recent async-opponent playerIds. Sent as `exclude=` on
// the next fetch so the same real player doesn't queue back-to-back. In
// memory only (resets on reload) — rematch prevention is a UX nicety,
// not a correctness requirement.
const RECENT_OPPONENT_LIMIT = 5;
const recentOpponentIds: string[] = [];
function rememberOpponentId(id: string) {
  const dedup = recentOpponentIds.filter((existing) => existing !== id);
  dedup.push(id);
  while (dedup.length > RECENT_OPPONENT_LIMIT) dedup.shift();
  recentOpponentIds.length = 0;
  recentOpponentIds.push(...dedup);
}

/**
 * Rebalance the round's pairings when the human is diverted to an async
 * opponent. The AI we were paired with would otherwise sit out the round
 * (no damage either way) — instead we merge them with the existing ghost
 * (if any) so both get a real fight, or have them ghost-fight cleanly.
 *
 * The human's own pair is replaced with (human, ASYNC_OPPONENT_ID). That
 * keeps the runRoundTick loop's `involvesHuman` branch firing so the
 * human still takes damage on a loss — but credits player-win damage to
 * a sentinel id that doesn't exist in `opponents`, so no real AI takes
 * an unearned hit.
 */
export function rebalancePairingsForAsync(
  pairings: Pairing[],
  humanId: string,
): Pairing[] {
  const humanIdx = pairings.findIndex(
    (p) => p.playerAId === humanId || p.playerBId === humanId,
  );
  if (humanIdx < 0) return pairings; // defensive: shouldn't happen
  const humanPair = pairings[humanIdx];
  const wasGhost = humanPair.playerAId === humanPair.playerBId;

  const next: Pairing[] = pairings.map((p, i) =>
    i === humanIdx ? { playerAId: humanId, playerBId: ASYNC_OPPONENT_ID } : p,
  );
  if (wasGhost) return next;

  const displacedId =
    humanPair.playerAId === humanId ? humanPair.playerBId : humanPair.playerAId;

  // Find the existing ghost (lone AI). Skip the human's pair so we don't
  // pick our own substituted ASYNC pair (which looks non-ghost anyway).
  const ghostIdx = next.findIndex(
    (p, i) => i !== humanIdx && p.playerAId === p.playerBId,
  );
  if (ghostIdx >= 0) {
    const ghostId = next[ghostIdx].playerAId;
    next[ghostIdx] = { playerAId: displacedId, playerBId: ghostId };
  } else {
    next.push({ playerAId: displacedId, playerBId: displacedId });
  }
  return next;
}

// Match state — scoped to a single run. Resets on endMatch().
// Keyed by matchId in the URL; if URL matchId !== store matchId, the
// scene route components redirect back to /play (deep-link guard).
//
// R7: the lobby is now 8 players. The human stays in this store's
// flat fields (board/hand/gold/health). The 7 AI opponents live in
// `opponents` as AIPlayer objects. Each round, `endTurn` pairs them
// up — the human's opponent's board is exposed as `opponentBoard`
// so CombatPage can render it unchanged. `applyCombatDamage` runs
// the full round tick (damage all 8, eliminate, advance AI taverns).

export type MatchPhase =
  | 'idle'
  | 'hero-select'
  | 'draft'
  | 'tavern'
  | 'combat'
  | 'results';

export const MAX_PICKS = 3;
export const BOARD_SLOTS = 7;
export const STARTING_HEALTH = 30;
export const MAX_TAVERN_TIER = 6;
export const HUMAN_PLAYER_ID = 'P0';
export const TURN_TIMER_MS = 60_000;
export const HERO_OPTIONS_COUNT = 4;
export const HERO_REROLLS_PER_MATCH = 1;

export interface MatchState {
  matchId: string | null;
  heroId: HeroId | null;
  phase: MatchPhase;
  booster: Card[];
  picks: Card[];
  seed: number;

  // Hero draft — 4 random heroes offered at match start, 1 free reroll
  heroOptions: HeroId[];
  heroRerollsRemaining: number;

  // Tavern phase state — human player
  roundNumber: number;
  health: number;
  gold: number;
  tavernTier: number;
  turnsAtCurrentTier: number;
  hand: Card[];
  board: (Card | null)[];
  shop: Card[];
  shopFrozen: boolean;
  refreshCount: number;
  /**
   * Free-refresh credits left this turn, granted by units with a
   * `grant_free_refresh` passive (Pasador de Backstage, Afinador de Beats).
   * Recomputed from the board at the start of every tavern round and spent
   * before gold is.
   */
  freeRefreshCredits: number;
  /** Units bought / played this turn — read by counter-based abilities. */
  buysThisTurn: number;
  playsThisTurn: number;

  // Combat phase state
  opponentBoard: Card[];
  lastCombatResult: CombatResult | null;
  combatResolved: boolean; // true once damage has been applied for the round

  // 8-player lobby
  opponents: AIPlayer[];
  currentOpponentIdx: number; // index into `opponents`, or -1 for ghost/none
  pairings: Pairing[];        // current round's pairings
  roundResults: PairingOutcome[]; // accumulated AI-vs-AI outcomes
  placement: number | null;   // 1..8, set on death or win

  // Triples auto-detect / Discover
  pendingTripleReward: { tier: number } | null;
  discoverOptions: Card[];

  // Card-ability Discover (Reclutador de Cuadra, Traficante de Mercado…).
  // Kept separate from the triple reward so the two can queue behind each
  // other on the same play instead of racing for one modal.
  pendingAbilityDiscover: { tier: number; tribe?: TribeId; sourceName: string } | null;
  abilityDiscoverOptions: Card[];

  // Turn timer
  turnTimerMs: number;
  turnTimerActive: boolean;

  // Hero power — active variant flow. Passives are not represented
  // here; they run inside the v1 engine via syncHumanToV1. The button
  // in the tavern rail should be disabled when state is 'used' or
  // when the hero's power is passive.
  heroPowerState: 'idle' | 'targeting' | 'discovering' | 'used';
  heroPowerTargetType: 'unit' | 'shop' | null;
  heroPowerDiscoverOptions: Card[];
  // Mirrors v1's PlayerState.heroPowerUsedThisTurn for the human so v2
  // UI can render without reaching into v1 state directly.
  heroPowerUsedThisTurn: boolean;

  // Async multiplayer — when populated, the human fights this real
  // player's snapshot instead of the AI opponent for the current round.
  // Cleared on round commit. Null means we're fighting the AI fallback.
  asyncOpponent: OpponentSnapshot | null;
  // Snapshot id returned from POST /snapshots, kept for the duration of
  // the current round so /matches/simulate can be called with it. Null
  // when offline or publish failed. Cleared on round commit.
  lastPublishedSnapshotId: string | null;
  // True iff the current round's combat was simulated server-side. The
  // server records the result during sim, so applyCombatDamage skips
  // the client-side POST /matches/result to avoid double-counting.
  // Transient — reset on each round commit.
  combatRemoteSimSucceeded: boolean;

  // Transient UI feedback events (buff flashes, heal/gold floaters).
  // Appended by tavern actions, played by TavernPage, never persisted.
  tavernFx: TavernFxEvent[];
}

interface MatchActions {
  newMatch: () => string;
  selectHero: (heroId: HeroId) => void;
  rerollHeroOptions: () => void;
  setBooster: (cards: Card[]) => void;
  togglePick: (card: Card) => void;
  confirmDraft: () => void;
  initTavern: () => void;
  buyFromShop: (instanceId: string) => void;
  playFromHand: (handIdx: number, slotIdx?: number) => void;
  sellFromBoard: (slotIdx: number) => void;
  reorderBoard: (from: number, to: number) => void;
  reroll: () => void;
  toggleFreeze: () => void;
  upgradeTavern: () => void;
  upgradeCost: () => number;
  endTurn: () => void;
  runCombat: () => Promise<CombatResult>;
  applyCombatDamage: () => 'continue' | 'eliminated' | 'victory';
  claimDiscoverReward: (card: Card) => void;
  claimAbilityDiscover: (card: Card) => void;
  tick: (deltaMs: number) => void;
  endMatch: () => void;

  // Hero power actions.
  activateHeroPower: () => void;
  selectHeroPowerTarget: (idx: number) => void;
  claimHeroPowerDiscover: (card: Card) => void;
  cancelHeroPower: () => void;
}

function emptyTavern() {
  return {
    roundNumber: 1,
    health: STARTING_HEALTH,
    gold: 0,
    tavernTier: 1,
    turnsAtCurrentTier: 0,
    hand: [] as Card[],
    board: Array(BOARD_SLOTS).fill(null) as (Card | null)[],
    shop: [] as Card[],
    shopFrozen: false,
    refreshCount: 0,
    freeRefreshCredits: 0,
    buysThisTurn: 0,
    playsThisTurn: 0,
    opponentBoard: [] as Card[],
    lastCombatResult: null as CombatResult | null,
    combatResolved: false,
    opponents: [] as AIPlayer[],
    currentOpponentIdx: -1,
    pairings: [] as Pairing[],
    roundResults: [] as PairingOutcome[],
    placement: null as number | null,
    pendingTripleReward: null as { tier: number } | null,
    discoverOptions: [] as Card[],
    pendingAbilityDiscover: null as
      | { tier: number; tribe?: TribeId; sourceName: string }
      | null,
    abilityDiscoverOptions: [] as Card[],
    turnTimerMs: TURN_TIMER_MS,
    turnTimerActive: false,
    heroPowerState: 'idle' as 'idle' | 'targeting' | 'discovering' | 'used',
    heroPowerTargetType: null as 'unit' | 'shop' | null,
    heroPowerDiscoverOptions: [] as Card[],
    heroPowerUsedThisTurn: false,
    asyncOpponent: null as OpponentSnapshot | null,
    lastPublishedSnapshotId: null as string | null,
    combatRemoteSimSucceeded: false,
    tavernFx: [] as TavernFxEvent[],
  };
}

// Append FX events, capping the buffer so an unplayed backlog (e.g. the
// tavern page unmounted) can't grow unbounded.
function pushFx(current: TavernFxEvent[], events: TavernFxEvent[]): TavernFxEvent[] {
  if (events.length === 0) return current;
  return [...current, ...events].slice(-20);
}

function goldForRound(round: number): number {
  return Math.min(MAX_GOLD, round + BASE_GOLD_PER_TURN);
}

// Sync the v2 human player snapshot into the v1 gameState singleton so
// combatEngine.simulateCombat and heroPowers.* see real data. Called
// just before runCombat. We don't sync after combat — v2 store stays
// the source of truth for the next tavern phase.
function syncHumanToV1(state: MatchState) {
  if (!v1GameState.players[0]) return;
  v1GameState.players[0].board = [...state.board] as any;
  v1GameState.players[0].hand = [...state.hand] as any;
  v1GameState.players[0].health = state.health;
  v1GameState.players[0].gold = state.gold;
  v1GameState.players[0].tavernTier = state.tavernTier;
  v1GameState.players[0].turn = state.roundNumber;
  v1GameState.players[0].alive = state.health > 0;
  v1GameState.roundNumber = state.roundNumber;
}

/** Build a transient PlayerState[] suitable for generatePairings. */
function buildPairingPool(
  humanId: string,
  humanAlive: boolean,
  humanBoard: (Card | null)[],
  opponents: AIPlayer[],
): PlayerState[] {
  const humanPS: PlayerState = {
    id: humanId,
    name: 'You',
    isHuman: true,
    health: 30,
    gold: 0,
    tavernTier: 1,
    turn: 0,
    board: humanBoard,
    hand: [],
    hero: null,
    alive: humanAlive,
    heroPowerUsedThisTurn: false,
    combatHistory: [],
    refreshCountThisTurn: 0,
    boardState: 'editing',
    shopFrozen: false,
    frozenShopCards: [],
  };
  const aiPS: PlayerState[] = opponents.map((o) => ({
    id: o.id,
    name: o.name,
    isHuman: false,
    health: o.health,
    gold: o.gold,
    tavernTier: o.tavernTier,
    turn: 0,
    board: o.board,
    hand: o.hand,
    hero: o.hero,
    alive: o.alive,
    heroPowerUsedThisTurn: o.heroPowerUsedThisTurn,
    combatHistory: [],
    refreshCountThisTurn: o.refreshCountThisTurn,
    boardState: 'editing',
    shopFrozen: false,
    frozenShopCards: [],
  }));
  return [humanPS, ...aiPS];
}

/**
 * Seeded discover options: shuffles cardsByTier[tier] via mulberry32 from
 * the current match seed (mixed with round + tier so successive triples
 * in one match produce different rolls), takes up to 3 with fresh
 * instanceIds. Falls back one tier down if the bucket is empty so a
 * misconfigured/empty tier never crashes the store. If all the way down
 * to tier 1 is empty (shouldn't happen in real data), returns [].
 *
 * `tribe` narrows the pool to one sub-tribe (Reclutador de Cuadra's
 * "Discover a unit of your chosen tribe"). Because a single tier rarely
 * holds three units of one tribe, a tribe discover widens its search up
 * through tier `tier` rather than looking at that tier alone, and drops
 * the tribe filter entirely if that still finds nothing.
 */
function getDiscoverOptionsForTier(
  tier: number,
  seed: number,
  salt: number,
  count: number = 3,
  tribe?: TribeId,
): Card[] {
  let pool: Card[];

  if (tribe) {
    // Everything of that tribe at or below the requested tier.
    pool = [];
    for (let t = tier; t >= 1; t--) {
      pool.push(...(cardsByTier[t] || []).filter((c) => c.tribe === tribe));
    }
    // No tribe members in range — fall back to a plain tier discover so the
    // battlecry still pays out instead of silently doing nothing.
    if (pool.length === 0) return getDiscoverOptionsForTier(tier, seed, salt, count);
  } else {
    let activeTier = tier;
    pool = cardsByTier[activeTier] || [];
    while (pool.length === 0 && activeTier > 1) {
      activeTier -= 1;
      pool = cardsByTier[activeTier] || [];
    }
  }

  if (pool.length === 0) return [];
  const rng = mulberry32((seed ^ (salt * 0x9e3779b9)) >>> 0);
  const shuffled = rng.shuffle(pool);
  return shuffled.slice(0, Math.min(count, shuffled.length)).map(newInstance);
}

/**
 * How many free shop refreshes the board grants this turn.
 *
 * Read straight off `grant_free_refresh` passives rather than dispatched
 * through the ability engine — a refresh credit is a property of the board,
 * not a reaction to an event, and the shop needs the number before any
 * trigger has fired. Hero-granted free refreshes (Don Promotor) stay in
 * `refreshCostForHero` and stack on top of this.
 */
function freeRefreshesFromBoard(board: (Card | null)[]): number {
  let credits = 0;
  for (const card of board) {
    for (const a of card?.abilities ?? []) {
      if (a.id === 'grant_free_refresh') credits += a.params?.count ?? 1;
    }
  }
  return credits;
}

/**
 * Applies a `drain_opponent_gold` result (La Karen) to the lobby.
 *
 * Battlecries fire during the tavern phase, before the round's pairings
 * exist, so there is no single "your opponent" to point at yet. The drain
 * lands on the living AI sitting on the most gold — deterministic, and it
 * bites the economy that most deserves biting.
 */
function drainOpponentGold(opponents: AIPlayer[], amount: number): AIPlayer[] {
  if (amount <= 0) return opponents;
  let richest = -1;
  for (let i = 0; i < opponents.length; i++) {
    if (!opponents[i].alive) continue;
    if (richest < 0 || opponents[i].gold > opponents[richest].gold) richest = i;
  }
  if (richest < 0) return opponents;
  return opponents.map((o, i) =>
    i === richest ? { ...o, gold: Math.max(0, o.gold - amount) } : o,
  );
}

/**
 * Bundles the turn-scoped facts abilities can read into the shape
 * fireTavernTrigger forwards to TriggerContext. `over` lets a caller
 * report a counter it is about to increment, so the ability firing on that
 * action sees the post-action value.
 */
function turnFacts(
  state: { buysThisTurn: number; playsThisTurn: number; roundNumber: number; heroId: HeroId | null },
  over: { buysThisTurn?: number; playsThisTurn?: number } = {},
): TavernTriggerFacts {
  const hero = state.heroId ? allHeroes.find((h) => h.id === state.heroId) : null;
  return {
    buysThisTurn: over.buysThisTurn ?? state.buysThisTurn,
    playsThisTurn: over.playsThisTurn ?? state.playsThisTurn,
    roundNumber: state.roundNumber,
    heroPowerBuffsUnits: !!hero && UNIT_BUFFING_POWERS.has(hero.powerType),
  };
}

/** Power types that hand stats to units — read by Estudiante Novata. */
const UNIT_BUFFING_POWERS = new Set([
  'passive_on_buy',
  'passive_frontline_health',
  'passive_end_of_turn',
  'active_target_buff',
  'passive_start_of_combat_attack',
  'passive_on_combat_end_buff',
  'passive_on_damage_dealt',
  'passive_token_buff',
  // A buff that expires at the end of combat is still a buff, so the
  // burn-side powers count here too.
  'active_blood_price',
  'active_crew_surge',
  'active_move_and_buff',
  'passive_tribe_surge',
  'passive_start_of_combat_backline',
  'passive_unspent_gold_invest',
]);

/**
 * Turn a `pendingDiscover` reported by fireTavernTrigger into the store
 * patch that opens the ability Discover modal. Returns an empty patch when
 * nothing is owed, or when the roll came back empty (no crash, no stuck
 * modal — the battlecry just fizzles).
 */
function abilityDiscoverPatch(
  pending: PendingDiscover | null | undefined,
  seed: number,
  salt: number,
): {
  pendingAbilityDiscover?: { tier: number; tribe?: TribeId; sourceName: string } | null;
  abilityDiscoverOptions?: Card[];
} {
  if (!pending) return {};
  const options = getDiscoverOptionsForTier(pending.tier, seed, salt, 3, pending.tribe);
  if (options.length === 0) return {};
  return { pendingAbilityDiscover: pending, abilityDiscoverOptions: options };
}

/**
 * Look at hand + board for triples. If one is found, remove the 3 source
 * cards (from wherever they live), add a golden to hand, and queue a
 * Discover reward at tier+1. Returns the next state patch. Returns null
 * if no triple is found.
 *
 * Only resolves ONE triple per call — chained triples naturally
 * re-trigger on the next user action (buy/play).
 */
function scanAndProcessTriples(
  hand: Card[],
  board: (Card | null)[],
  seed: number,
  roundNumber: number,
): null | {
  hand: Card[];
  board: (Card | null)[];
  pendingTripleReward: { tier: number };
  discoverOptions: Card[];
} {
  const handSize = hand.length;
  const combined: (Card | null)[] = [...hand, ...board];
  const triples = findTripleIndices(combined);
  if (triples.length === 0) return null;

  const triple = triples[0];
  const tripleCards = triple.map((i) => combined[i]).filter(Boolean) as Card[];
  if (tripleCards.length !== 3) return null;

  const baseCard = tripleCards[0];
  const golden = createGoldenFromTriple(tripleCards, baseCard);
  const rewardTier = getTripleRewardTier(baseCard.tier);

  // Remove sources. Sort descending for hand splice; just null-out board.
  const handIdxs: number[] = [];
  const boardIdxs: number[] = [];
  triple.forEach((idx) => {
    if (idx < handSize) handIdxs.push(idx);
    else boardIdxs.push(idx - handSize);
  });

  const nextHand = [...hand];
  handIdxs.sort((a, b) => b - a).forEach((i) => nextHand.splice(i, 1));
  nextHand.push(golden);

  const nextBoard = [...board];
  boardIdxs.forEach((i) => {
    nextBoard[i] = null;
  });

  // Salt the discover roll so multiple triples in one round don't
  // produce identical options.
  const salt = roundNumber * 1000 + rewardTier * 31 + handIdxs.length;
  const discoverOptions = getDiscoverOptionsForTier(rewardTier, seed, salt, 3);

  return {
    hand: nextHand,
    board: nextBoard,
    pendingTripleReward: { tier: rewardTier },
    discoverOptions,
  };
}

// Pick N unique heroes at random from the full roster. `exclude` lets a
// reroll skip the heroes the player just declined so they always see
// fresh faces.
function pickHeroOptions(count: number, exclude: ReadonlySet<HeroId> = new Set()): HeroId[] {
  const pool = allHeroes.filter((h) => !exclude.has(h.id) && !h.disabled);
  // If the exclusion list would leave fewer than `count`, fall back to
  // the full roster (happens only at very small rosters — defensive).
  const source = pool.length >= count ? pool : allHeroes;
  const shuffled = [...source];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count).map((h) => h.id);
}

// Bump when the persisted MatchState shape changes incompatibly. The
// migrate hook below drops stale entries by returning undefined so the
// store falls back to the create() initializer.
const MATCH_SCHEMA_VERSION = 1;

export const useMatchStore = create<MatchState & MatchActions>()(
  persist(
    (set, get) => ({
  matchId: null,
  heroId: null,
  phase: 'idle',
  booster: [],
  picks: [],
  seed: 0,
  heroOptions: [],
  heroRerollsRemaining: HERO_REROLLS_PER_MATCH,
  ...emptyTavern(),

  newMatch: () => {
    const id = `m_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    set({
      matchId: id,
      heroId: null,
      phase: 'hero-select',
      booster: [],
      picks: [],
      seed: freshSeed(),
      heroOptions: pickHeroOptions(HERO_OPTIONS_COUNT),
      heroRerollsRemaining: HERO_REROLLS_PER_MATCH,
      ...emptyTavern(),
    });
    return id;
  },

  rerollHeroOptions: () => {
    const { heroRerollsRemaining, heroOptions } = get();
    if (heroRerollsRemaining <= 0) return;
    set({
      heroOptions: pickHeroOptions(HERO_OPTIONS_COUNT, new Set(heroOptions)),
      heroRerollsRemaining: heroRerollsRemaining - 1,
    });
  },

  selectHero: (heroId) =>
    set({ heroId, phase: 'draft', booster: [], picks: [], ...emptyTavern() }),

  setBooster: (cards) => set({ booster: cards }),

  togglePick: (card) => {
    const { picks } = get();
    const idx = picks.findIndex((p) => p.id === card.id);
    if (idx >= 0) {
      set({ picks: picks.filter((_, i) => i !== idx) });
    } else if (picks.length < MAX_PICKS) {
      set({ picks: [...picks, card] });
    }
  },

  confirmDraft: () => set({ phase: 'tavern' }),

  initTavern: () => {
    const { picks, heroId, seed } = get();
    const hero = allHeroes.find((h) => h.id === heroId);
    if (hero) {
      // Seed v1 singleton so combat-time helpers (getCurrentHero, etc.) work.
      initNewLobby(hero, picks.map((c) => c.id));
    }
    const { opponents } = createLobby(heroId, picks, seed);
    set({
      ...emptyTavern(),
      roundNumber: 1,
      gold: goldForRound(1),
      tavernTier: 1,
      hand: picks.map(newInstance),
      shop: rollShop(1),
      opponents,
      turnTimerMs: TURN_TIMER_MS,
      turnTimerActive: true,
    });
  },

  buyFromShop: (instanceId) => {
    const state = get();
    const { shop, gold, hand, board, seed, roundNumber } = state;
    const cardIdx = shop.findIndex((c) => c.instanceId === instanceId);
    if (cardIdx === -1) return;
    const cost = 3;
    if (gold < cost) return;
    const card = shop[cardIdx];
    const filteredShop = [...shop.slice(0, cardIdx), ...shop.slice(cardIdx + 1)];
    const nextHand = [...hand, card];

    // Fire on_buy hooks on board units (e.g. "Whenever you buy a card,
    // gain +1 Attack") before the triple scan. The buy counter is bumped
    // first so a card that reads "if you bought 3 or more units this turn"
    // sees the purchase that just happened.
    const buysThisTurn = state.buysThisTurn + 1;
    const triggered = fireTavernTrigger(
      'on_buy',
      occupiedSlots(board),
      {
        board,
        hand: nextHand,
        shop: filteredShop,
        gold: gold - cost,
        health: state.health,
      },
      Math.random,
      turnFacts(state, { buysThisTurn }),
    );

    // Hero on-buy passives (El Profe del Punk, Barrio Godfather). The v1
    // helper mutates cards in place, so hand it fresh clones.
    const heroBoard = triggered.board.map((c) => (c ? { ...c } : null));
    applyOnBuyHeroPower(heroBoard, card);
    triggered.board = heroBoard;

    const fx = diffTavernFx(
      { board, shop, health: state.health },
      { board: triggered.board, shop: triggered.shop, health: triggered.health },
    );

    // Scan for triples after the buy. If one resolves it'll mutate hand
    // (golden in, 3 sources out) and possibly board.
    const tripleResult = scanAndProcessTriples(
      triggered.hand,
      triggered.board,
      seed,
      roundNumber,
    );
    if (tripleResult) {
      set({
        shop: triggered.shop,
        gold: triggered.gold,
        health: triggered.health,
        hand: tripleResult.hand,
        board: tripleResult.board,
        pendingTripleReward: tripleResult.pendingTripleReward,
        discoverOptions: tripleResult.discoverOptions,
        tavernFx: pushFx(state.tavernFx, fx),
        buysThisTurn,
      });
    } else {
      set({
        shop: triggered.shop,
        hand: triggered.hand,
        board: triggered.board,
        gold: triggered.gold,
        buysThisTurn,
        health: triggered.health,
        tavernFx: pushFx(state.tavernFx, fx),
      });
    }
  },

  playFromHand: (handIdx, slotIdx) => {
    const state = get();
    const { hand, board, seed, roundNumber } = state;
    if (handIdx < 0 || handIdx >= hand.length) return;
    const card = hand[handIdx];
    const targetSlot =
      typeof slotIdx === 'number'
        ? slotIdx
        : board.findIndex((s) => s === null);
    if (targetSlot < 0 || targetSlot >= BOARD_SLOTS || board[targetSlot] !== null) return;
    const nextBoard = [...board];
    nextBoard[targetSlot] = card;
    const nextHand = hand.filter((_, i) => i !== handIdx);

    // Battlecry: fire the played card's "summon" abilities now that it's
    // on the board (buffs, token summons, hero heal, shop buffs, ...).
    // Counted first so "whenever you play your third unit in a turn" sees
    // the unit that is being played right now.
    const playsThisTurn = state.playsThisTurn + 1;
    const triggered = fireTavernTrigger(
      'summon',
      [targetSlot],
      {
        board: nextBoard,
        hand: nextHand,
        shop: state.shop,
        gold: state.gold,
        health: state.health,
      },
      Math.random,
      turnFacts(state, { playsThisTurn }),
    );

    // Diff against the board WITH the card already placed, so battlecries
    // that change the played card itself (stat swaps) flash too.
    const fx = diffTavernFx(
      { board: nextBoard, shop: state.shop, health: state.health },
      { board: triggered.board, shop: triggered.shop, health: triggered.health },
    );

    // Battlecry Discover (Reclutador de Cuadra). Salted off the slot so
    // playing two recruiters in one turn rolls different options. If a
    // triple also formed, TavernPage holds this modal until that one is
    // claimed — the state can coexist, the UI queues.
    const discoverPatch = abilityDiscoverPatch(
      triggered.pendingDiscover,
      seed,
      roundNumber * 977 + targetSlot * 13 + 7,
    );

    // La Karen: skim gold off the richest living AI.
    const opponentsPatch = triggered.opponentGoldDrain
      ? { opponents: drainOpponentGold(state.opponents, triggered.opponentGoldDrain) }
      : {};

    const tripleResult = scanAndProcessTriples(
      triggered.hand,
      triggered.board,
      seed,
      roundNumber,
    );
    if (tripleResult) {
      set({
        shop: triggered.shop,
        gold: triggered.gold,
        health: triggered.health,
        hand: tripleResult.hand,
        board: tripleResult.board,
        pendingTripleReward: tripleResult.pendingTripleReward,
        discoverOptions: tripleResult.discoverOptions,
        tavernFx: pushFx(state.tavernFx, fx),
        playsThisTurn,
        ...discoverPatch,
        ...opponentsPatch,
      });
    } else {
      set({
        hand: triggered.hand,
        board: triggered.board,
        shop: triggered.shop,
        gold: triggered.gold,
        health: triggered.health,
        tavernFx: pushFx(state.tavernFx, fx),
        playsThisTurn,
        ...discoverPatch,
        ...opponentsPatch,
      });
    }
  },

  sellFromBoard: (slotIdx) => {
    const state = get();
    const { board, gold } = state;
    if (slotIdx < 0 || slotIdx >= BOARD_SLOTS) return;
    if (!board[slotIdx]) return;
    const nextBoard = [...board];
    nextBoard[slotIdx] = null;

    // Fire on_sell hooks on the units still on the board (e.g. Botellero:
    // "Minions you sell give +1 extra gold").
    const triggered = fireTavernTrigger('on_sell', occupiedSlots(nextBoard), {
      board: nextBoard,
      hand: state.hand,
      shop: state.shop,
      gold: gold + 1,
      health: state.health,
    });

    const fx = diffTavernFx(
      { board: nextBoard, shop: state.shop, health: state.health },
      { board: triggered.board, shop: triggered.shop, health: triggered.health },
    );

    set({
      board: triggered.board,
      hand: triggered.hand,
      shop: triggered.shop,
      gold: triggered.gold,
      health: triggered.health,
      tavernFx: pushFx(state.tavernFx, fx),
    });
  },

  reorderBoard: (from, to) => {
    const { board } = get();
    if (from < 0 || from >= BOARD_SLOTS) return;
    if (to < 0 || to >= BOARD_SLOTS) return;
    if (from === to) return;
    const src = board[from];
    if (src === null) return;
    const dest = board[to];
    const nextBoard = [...board];
    if (dest === null) {
      // simple move
      nextBoard[to] = src;
      nextBoard[from] = null;
    } else {
      // swap
      nextBoard[to] = src;
      nextBoard[from] = dest;
    }
    set({ board: nextBoard });
  },

  reroll: () => {
    const { gold, tavernTier, refreshCount, heroId, board, health, tavernFx, freeRefreshCredits } = get();
    const hero = getHeroById(heroId);
    // Don Promotor: first refresh each turn is free.
    let cost = refreshCostForHero(hero, 1, refreshCount);
    // Board-granted free refreshes are spent before gold. Only consume a
    // credit when the refresh would otherwise have cost something, so a
    // hero-free first refresh doesn't silently burn one.
    let creditsLeft = freeRefreshCredits;
    if (cost > 0 && creditsLeft > 0) {
      creditsLeft -= 1;
      cost = 0;
    }
    if (gold < cost) return;
    // El Profe / La DJ del Toquín: a random unit in the new shop gets +1/+1.
    const rolled = rollShop(tavernTier);
    const shop = applyOnRefreshHeroPower(hero, rolled);
    const fx = diffTavernFx(
      { board, shop: rolled, health },
      { board, shop, health },
    );
    set({
      gold: gold - cost,
      shop,
      refreshCount: refreshCount + 1,
      freeRefreshCredits: creditsLeft,
      tavernFx: pushFx(tavernFx, fx),
    });
  },

  toggleFreeze: () => set({ shopFrozen: !get().shopFrozen }),

  upgradeCost: () => {
    const { tavernTier, turnsAtCurrentTier, heroId } = get();
    const nextTier = tavernTier + 1;
    if (nextTier > MAX_TAVERN_TIER) return 0;
    const base = TAVERN_UPGRADE_COSTS[nextTier] ?? 0;
    // La Patrona's flat discount stacks on top of the standard
    // "cheaper the longer you sit" decay.
    const afterDecay = Math.max(0, base - turnsAtCurrentTier);
    return tierUpCostForHero(getHeroById(heroId), afterDecay);
  },

  upgradeTavern: () => {
    const cost = get().upgradeCost();
    const state = get();
    const { gold, tavernTier, heroId, hand } = state;
    if (tavernTier >= MAX_TAVERN_TIER) return;
    if (gold < cost) return;

    const nextTier = tavernTier + 1;
    const hero = getHeroById(heroId);

    // RUSH payoff: arriving at a new tier hands you a body from it, so the
    // tier-up is a tempo play rather than a turn spent going broke.
    let nextHand = hand;
    if (grantsUnitOnTierUp(hero) && hand.length < HAND_LIMIT) {
      const pool = cardsByTier[nextTier] ?? [];
      if (pool.length > 0) {
        const rng = mulberry32(
          (state.seed ^ (nextTier * 0x85ebca6b) ^ (state.roundNumber * 0xc2b2ae35)) >>> 0,
        );
        nextHand = [...hand, newInstance(rng.pick(pool))];
      }
    }

    set({
      gold: gold - cost,
      tavernTier: nextTier,
      turnsAtCurrentTier: 0,
      hand: nextHand,
    });
  },

  endTurn: () => {
    const preTurnState = get();

    // Fire end_of_turn abilities (e.g. "End of turn: a random friendly
    // unit gains +1 Health") before pairing/publishing, so the buffed
    // board is what actually fights.
    const eotResult = fireTavernTrigger(
      'end_of_turn',
      occupiedSlots(preTurnState.board),
      {
        board: preTurnState.board,
        hand: preTurnState.hand,
        shop: preTurnState.shop,
        gold: preTurnState.gold,
        health: preTurnState.health,
      },
      Math.random,
      turnFacts(preTurnState),
    );

    // Hero end-of-turn passives (Block Captain's token, Block Chef's
    // +1 Health, Scene Guardian's heal). The v1 helper mutates cards and
    // board slots in place, so hand it fresh clones.
    const eotBoard = eotResult.board.map((c) => (c ? { ...c } : null));
    const eotHealth = applyEndOfTurnHeroPowers(eotBoard, eotResult.health, get().gold);

    // Tavern-phase tribe payoffs — PLACA tags the carry, RAZA grows the
    // swarm. Runs last so it sees the board the player actually committed,
    // including anything hero powers just added.
    const tribeEot = applyTribeEndOfTurn(eotBoard);
    if (tribeEot.log.length > 0) {
      console.log(`[tribes] ${tribeEot.log.join(' · ')}`);
    }

    set({
      board: tribeEot.board,
      hand: eotResult.hand,
      shop: eotResult.shop,
      gold: eotResult.gold,
      health: Math.min(STARTING_HEALTH, eotHealth),
    });

    const { roundNumber, opponents, board, health, tavernTier } = get();
    // Compute pairings for this round. We use the v1 helper so any
    // future seed-bump applies symmetrically to human + AIs.
    const pool = buildPairingPool(HUMAN_PLAYER_ID, health > 0, board, opponents);
    const pairings = generatePairings(pool, roundNumber);
    const myPairing = pairings.find(
      (p) => p.playerAId === HUMAN_PLAYER_ID || p.playerBId === HUMAN_PLAYER_ID,
    );
    let opponentBoard: Card[] = [];
    let currentOpponentIdx = -1;
    if (myPairing) {
      const isGhost = myPairing.playerAId === myPairing.playerBId;
      if (!isGhost) {
        const oppId =
          myPairing.playerAId === HUMAN_PLAYER_ID
            ? myPairing.playerBId
            : myPairing.playerAId;
        const idx = opponents.findIndex((o) => o.id === oppId);
        if (idx >= 0) {
          const opp = opponents[idx];
          const oppBoardCards = opp.board.filter(Boolean) as Card[];
          // Defensive fallback so the canvas never sees a 0-unit
          // enemy board (would feel like a forfeit visually).
          opponentBoard = oppBoardCards.length > 0
            ? oppBoardCards.map((c) => ({ ...c }))
            : generateOpponentBoard(roundNumber, tavernTier);
          currentOpponentIdx = idx;
        }
      }
    }
    set({
      pairings,
      opponentBoard,
      currentOpponentIdx,
      lastCombatResult: null,
      combatResolved: false,
      turnTimerActive: false,
      asyncOpponent: null,
    });

    // Async multiplayer — fire-and-forget. Publish our snapshot so other
    // players can fight us, and try to pull a real opponent snapshot. If
    // the fetch returns before runCombat fires, we replace opponentBoard;
    // otherwise the AI opponent stands. Failures are silent (offline play).
    const heroIdForPublish = get().heroId;
    if (heroIdForPublish) {
      const handle = useAccountStore.getState().handle;
      const filledBoard = board.filter(Boolean) as Card[];
      const roundForPublish = roundNumber;
      publishSnapshot({
        roundNum: roundForPublish,
        heroId: heroIdForPublish,
        tavernTier,
        health,
        handle,
        board: filledBoard,
      })
        .then((snapshotId) => {
          if (!snapshotId) return;
          // Stash for /matches/simulate. Only valid for this round —
          // applyCombatDamage clears it on commit.
          const now = get();
          if (now.roundNumber !== roundForPublish) return;
          set({ lastPublishedSnapshotId: snapshotId });
        })
        .catch(() => {});

      fetchOpponentSnapshot(roundForPublish, recentOpponentIds)
        .then((snapshot) => {
          if (!snapshot || snapshot.board.length === 0) return;
          // Only apply if we're still in the same round and combat
          // hasn't resolved yet — otherwise this is a stale fetch.
          const now = get();
          if (now.roundNumber !== roundForPublish) return;
          if (now.combatResolved) return;
          rememberOpponentId(snapshot.playerId);
          set({
            asyncOpponent: snapshot,
            opponentBoard: snapshot.board.map((c) => ({ ...c })),
            // Rebalance pairings so the AI we displaced doesn't sit out
            // the round. See rebalancePairingsForAsync above.
            pairings: rebalancePairingsForAsync(now.pairings, HUMAN_PLAYER_ID),
          });
        })
        .catch(() => {});
    }
  },

  runCombat: async () => {
    const state = get();
    // Server-trusted path: if we're fighting an async opponent and both
    // snapshot ids are in hand, ask the server to simulate. The server
    // re-fetches both boards from the DB so a tampered local `state.board`
    // can't change the outcome. On any failure (offline, snapshot
    // expired, 4xx/5xx), we fall back to client sim so the round still
    // completes — game stays playable in offline mode.
    if (state.asyncOpponent && state.lastPublishedSnapshotId) {
      const remote = await simulateCombatRemote(
        state.lastPublishedSnapshotId,
        state.asyncOpponent.id,
      );
      if (remote) {
        syncHumanToV1(state); // keep v1 singleton coherent for ancillary reads
        set({ lastCombatResult: remote, combatRemoteSimSucceeded: true });
        return remote;
      }
      // fall through to client sim
    }
    syncHumanToV1(state);
    const result = simulateCombat(state.board, state.opponentBoard);
    set({ lastCombatResult: result });
    return result;
  },

  applyCombatDamage: () => {
    const state = get();
    const {
      lastCombatResult,
      health,
      roundNumber,
      shopFrozen,
      shop,
      tavernTier,
      turnsAtCurrentTier,
      combatResolved,
      opponents,
      currentOpponentIdx,
      pairings,
      roundResults,
      board,
      seed,
    } = state;
    if (!lastCombatResult || combatResolved) {
      if (health <= 0) return 'eliminated';
      const aliveAIs = opponents.filter((o) => o.alive).length;
      if (aliveAIs === 0) return 'victory';
      return 'continue';
    }

    const opponentId = state.asyncOpponent
      ? ASYNC_OPPONENT_ID
      : currentOpponentIdx >= 0 && opponents[currentOpponentIdx]
        ? opponents[currentOpponentIdx].id
        : HUMAN_PLAYER_ID; // ghost-fight sentinel

    // Run the round tick. Pass the already-resolved human result so we
    // don't double-simulate, and the pre-computed pairings so the tick
    // agrees with what `endTurn` showed the human.
    const rng = mulberry32(seed ^ ((roundNumber + 1) * 0x9e3779b9));
    const tick = runRoundTick(
      {
        id: HUMAN_PLAYER_ID,
        health,
        board,
        alive: health > 0,
      },
      opponents,
      roundNumber,
      rng,
      { result: lastCombatResult, opponentId },
      pairings.length > 0 ? pairings : undefined,
    );

    // Apply all damage. AI damage was already applied inside the tick
    // and reflected in `tick.opponentsAfter`. Human damage we apply here.
    const humanDamage = tick.damageApplied[HUMAN_PLAYER_ID] ?? 0;
    const nextHealth = Math.max(0, health - humanDamage);
    const humanEliminated = nextHealth <= 0;

    // Compute placement counts. v1 doesn't track placement; we infer
    // from current alive count at the moment of elimination.
    let placement: number | null = state.placement;
    let opponentsAfter = tick.opponentsAfter;

    // Mark human as dead in our local view (we don't track human in
    // opponents, so just record placement).
    const aliveAIsAfter = opponentsAfter.filter((o) => o.alive).length;
    const aliveCountAfter = aliveAIsAfter + (humanEliminated ? 0 : 1);

    if (humanEliminated && placement === null) {
      // Place = 1 + number of survivors after this tick.
      placement = aliveCountAfter + 1;
    }

    // Victory: only the human survives.
    let outcome: 'continue' | 'eliminated' | 'victory' = 'continue';
    if (humanEliminated) {
      outcome = 'eliminated';
    } else if (aliveAIsAfter === 0) {
      outcome = 'victory';
      placement = 1;
    }

    const nextRound = roundNumber + 1;

    // Sync the human's hero-power-used flag back from v1. v1's
    // `runTavernAI` resets `heroPowerUsedThisTurn` for AIs each round
    // tick; the human's flag is reset here so the tavern rail re-enables
    // the power button.
    if (v1GameState.players[0]) {
      v1GameState.players[0].heroPowerUsedThisTurn = false;
    }

    // If we fought a real player's snapshot AND the server didn't already
    // record the result (server-sim writes match_results inline), post
    // the result so the opponent can derive ladder/stats from it later.
    if (state.asyncOpponent && !state.combatRemoteSimSucceeded) {
      const oppDamage = tick.damageApplied[ASYNC_OPPONENT_ID] ?? 0;
      const winner = lastCombatResult.winner;
      const resultOutcome: 'win' | 'loss' | 'tie' =
        winner === 'player' ? 'win' : winner === 'enemy' ? 'loss' : 'tie';
      recordMatchResult({
        opponentSnapshotId: state.asyncOpponent.id,
        roundNum: roundNumber,
        outcome: resultOutcome,
        damageDealt: oppDamage,
        damageTaken: humanDamage,
      }).catch(() => {});
    }

    // Post-combat hero passives (El Tagger's random +1/+1, Old School
    // Writer's +1 Attack to units that dealt damage). Clone cards first —
    // the v1 helper mutates in place. Remote sims serialize the result,
    // so the damage-dealer Set may be absent there.
    const postCombatBoard = clearCombatBuffs(board).map((c) =>
      c ? { ...c } : null,
    );
    const damageDealers =
      lastCombatResult.playerUnitsThatDealtDamage instanceof Set
        ? lastCombatResult.playerUnitsThatDealtDamage
        : new Set<string>();
    applyPostCombatHeroPowers(postCombatBoard, damageDealers);

    // FX for the new tavern round: post-combat hero buffs (El Tagger,
    // Veterano) + start-of-turn gold (El Festivalero). Health is passed
    // as unchanged — combat damage has its own presentation.
    const baseGold = goldForRound(nextRound);
    const newTurnGold = applyStartOfTurnHeroPowers(baseGold);

    // Fire start_of_turn against the shop the player is about to see, so
    // "give a random unit in the shop +1 Health" (Becaria del Hype) lands
    // on cards they can actually buy rather than the ones just replaced.
    const nextShop = shopFrozen ? shop : rollShop(tavernTier);
    const sot = fireTavernTrigger(
      'start_of_turn',
      occupiedSlots(postCombatBoard),
      {
        board: postCombatBoard,
        hand: state.hand,
        shop: nextShop,
        gold: newTurnGold,
        health: nextHealth,
      },
      Math.random,
      { roundNumber: nextRound },
    );

    const fx = diffTavernFx(
      { board, shop: nextShop, health: 0 },
      { board: sot.board, shop: sot.shop, health: 0 },
    );
    if (newTurnGold > baseGold) fx.push(goldGainFx(newTurnGold - baseGold));

    set({
      health: nextHealth,
      combatResolved: true,
      // "This combat" buffs granted during the tavern phase are spent.
      board: sot.board,
      hand: sot.hand,
      roundNumber: nextRound,
      // El Festivalero: +1 gold at the start of the turn (max 10).
      gold: sot.gold,
      tavernFx: pushFx(state.tavernFx, fx),
      shop: sot.shop,
      shopFrozen: false,
      refreshCount: 0,
      // Per-turn resources reset with the new tavern round.
      freeRefreshCredits: freeRefreshesFromBoard(sot.board),
      buysThisTurn: 0,
      playsThisTurn: 0,
      turnsAtCurrentTier: turnsAtCurrentTier + 1,
      opponentBoard: [],
      opponents: opponentsAfter,
      currentOpponentIdx: -1,
      pairings: [],
      roundResults: [...roundResults, ...tick.silentFights],
      placement,
      asyncOpponent: null,
      lastPublishedSnapshotId: null,
      combatRemoteSimSucceeded: false,
      // New tavern round begins — reset and start the timer.
      turnTimerMs: TURN_TIMER_MS,
      turnTimerActive: outcome === 'continue',
      // Reset hero power for the new round.
      heroPowerState: 'idle',
      heroPowerTargetType: null,
      heroPowerDiscoverOptions: [],
      heroPowerUsedThisTurn: false,
    });

    return outcome;
  },

  claimDiscoverReward: (card) => {
    const { hand, pendingTripleReward } = get();
    if (!pendingTripleReward) return;
    // Always give the player a fresh instanceId — `card` came from
    // discoverOptions and may share an instanceId with the rendered slot.
    set({
      hand: [...hand, newInstance(card)],
      pendingTripleReward: null,
      discoverOptions: [],
    });
  },

  claimAbilityDiscover: (card) => {
    const { hand, pendingAbilityDiscover } = get();
    if (!pendingAbilityDiscover) return;
    // Hand-limit guard: a full hand drops the pick rather than overflowing.
    // Same fresh-instanceId rule as the triple reward.
    set({
      hand: hand.length < HAND_LIMIT ? [...hand, newInstance(card)] : hand,
      pendingAbilityDiscover: null,
      abilityDiscoverOptions: [],
    });
  },

  tick: (deltaMs) => {
    const { turnTimerActive, turnTimerMs } = get();
    if (!turnTimerActive) return;
    const nextMs = Math.max(0, turnTimerMs - deltaMs);
    if (nextMs === 0) {
      // Time's up — end the turn (this also sets turnTimerActive=false
      // via endTurn). We update ms first so observers see the 0 frame.
      set({ turnTimerMs: 0 });
      get().endTurn();
      set({ turnTimerActive: false });
    } else {
      set({ turnTimerMs: nextMs });
    }
  },

  endMatch: () =>
    set({
      matchId: null,
      heroId: null,
      phase: 'idle',
      booster: [],
      picks: [],
      seed: 0,
      heroOptions: [],
      heroRerollsRemaining: HERO_REROLLS_PER_MATCH,
      ...emptyTavern(),
    }),

  /**
   * Entry point for the tavern-rail hero-power button. Reads the hero's
   * power type, classifies it, and routes:
   *   passive          -> no-op (button should already be disabled)
   *   active           -> apply immediately, deduct gold, mark used
   *   active targeted  -> enter 'targeting' state; resolution happens
   *                       in `selectHeroPowerTarget`
   *   active discover  -> enter 'discovering' state with 3 options
   *
   * Cost & per-round-use are enforced here so the button can sit in a
   * pure-render component without duplicating these checks.
   */
  activateHeroPower: () => {
    const state = get();
    if (state.heroPowerState !== 'idle') return; // already in a flow
    if (state.heroPowerUsedThisTurn) return;
    const hero = getHeroById(state.heroId);
    if (!hero) return;
    if (!hero.isActive) return;
    const cls = classifyPower(hero.powerType, hero.cost);
    if (cls.variant === 'passive') return;
    if (state.gold < cls.cost) return; // not enough gold
    // Blood-price powers (La Aguja) bill Health, not gold. Checked off
    // `powerConfig`, never off the hero's id — an id-keyed perk silently
    // transfers when a hero is re-rolled, which has bitten this file twice.
    const healthCost = hero.powerConfig?.healthCost ?? 0;
    if (healthCost > 0 && state.health - healthCost < 1) return;

    if (cls.variant === 'active') {
      // No-target — apply, deduct cost, mark used.
      const patch = applyActivePower({
        heroId: state.heroId,
        gold: state.gold,
        health: state.health,
        board: state.board,
        shop: state.shop,
        shopFrozen: state.shopFrozen,
      });
      if (!patch) return; // adapter said "no". Stay idle.
      // Sync v1 flag so any v1-side check (pre-combat passives) sees it.
      if (v1GameState.players[0]) {
        v1GameState.players[0].heroPowerUsedThisTurn = true;
      }
      set({
        ...patch,
        gold: state.gold - cls.cost,
        heroPowerState: 'used',
        heroPowerTargetType: null,
        heroPowerUsedThisTurn: true,
      });
      return;
    }

    if (cls.variant === 'targeted_unit' || cls.variant === 'targeted_shop') {
      // Don't deduct gold yet — let the player cancel without paying.
      set({
        heroPowerState: 'targeting',
        heroPowerTargetType: cls.targetType ?? null,
      });
      return;
    }

    if (cls.variant === 'discover') {
      // No discover-variant powers in v2 today. Reserved.
      set({
        heroPowerState: 'discovering',
        heroPowerDiscoverOptions: [],
      });
      return;
    }
  },

  /**
   * Player tapped a board slot or shop card while a targeted power was
   * armed. Resolves the effect, deducts cost, marks the power used.
   * No-ops if we're not in targeting state.
   */
  selectHeroPowerTarget: (idx) => {
    const state = get();
    if (state.heroPowerState !== 'targeting') return;
    const hero = getHeroById(state.heroId);
    if (!hero) return;
    const cls = classifyPower(hero.powerType, hero.cost);
    if (cls.variant !== 'targeted_unit' && cls.variant !== 'targeted_shop') return;
    if (state.gold < cls.cost) {
      // Player's gold dropped below cost while targeting (e.g., bought
      // a unit mid-flow). Cancel quietly.
      set({
        heroPowerState: 'idle',
        heroPowerTargetType: null,
      });
      return;
    }
    const patch = applyTargetedPower(
      {
        heroId: state.heroId,
        gold: state.gold,
        health: state.health,
        board: state.board,
        shop: state.shop,
        shopFrozen: state.shopFrozen,
      },
      idx,
    );
    if (!patch) {
      // Invalid target — stay in targeting so the player can pick again.
      return;
    }
    if (v1GameState.players[0]) {
      v1GameState.players[0].heroPowerUsedThisTurn = true;
    }
    set({
      ...patch,
      gold: state.gold - cls.cost,
      heroPowerState: 'used',
      heroPowerTargetType: null,
      heroPowerUsedThisTurn: true,
    });
  },

  /**
   * Player picked one of the 3 cards from a discover-variant power.
   * Adds the card (fresh instanceId) to hand, clears state.
   * No discover powers ship today — provided for parity with the
   * triples-discover contract.
   */
  claimHeroPowerDiscover: (card) => {
    const state = get();
    if (state.heroPowerState !== 'discovering') return;
    const hero = getHeroById(state.heroId);
    if (!hero) return;
    const cls = classifyPower(hero.powerType, hero.cost);
    if (v1GameState.players[0]) {
      v1GameState.players[0].heroPowerUsedThisTurn = true;
    }
    set({
      hand: [...state.hand, newInstance(card)],
      gold: Math.max(0, state.gold - cls.cost),
      heroPowerState: 'used',
      heroPowerDiscoverOptions: [],
      heroPowerUsedThisTurn: true,
    });
  },

  /**
   * Abort a targeting/discovering state back to idle. Cost was not yet
   * spent (we charge on resolve, not activate), so no refund is needed.
   * No-op if the power has already resolved ('used') or hasn't started.
   */
  cancelHeroPower: () => {
    const state = get();
    if (state.heroPowerState !== 'targeting' && state.heroPowerState !== 'discovering') {
      return;
    }
    set({
      heroPowerState: 'idle',
      heroPowerTargetType: null,
      heroPowerDiscoverOptions: [],
    });
  },
    }),
    {
      name: 'cve.v2.match',
      version: MATCH_SCHEMA_VERSION,
      // Persist data fields only, not actions. Also skip mid-action state
      // (heroPower targeting/discovering, turn timer) so a refresh always
      // returns the player to a clean tavern view instead of mid-modal.
      partialize: (state) => ({
        matchId: state.matchId,
        heroId: state.heroId,
        phase: state.phase,
        booster: state.booster,
        picks: state.picks,
        seed: state.seed,
        heroOptions: state.heroOptions,
        heroRerollsRemaining: state.heroRerollsRemaining,
        roundNumber: state.roundNumber,
        health: state.health,
        gold: state.gold,
        tavernTier: state.tavernTier,
        turnsAtCurrentTier: state.turnsAtCurrentTier,
        hand: state.hand,
        board: state.board,
        shop: state.shop,
        shopFrozen: state.shopFrozen,
        refreshCount: state.refreshCount,
        freeRefreshCredits: state.freeRefreshCredits,
        buysThisTurn: state.buysThisTurn,
        playsThisTurn: state.playsThisTurn,
        opponentBoard: state.opponentBoard,
        lastCombatResult: state.lastCombatResult,
        combatResolved: state.combatResolved,
        opponents: state.opponents,
        currentOpponentIdx: state.currentOpponentIdx,
        pairings: state.pairings,
        roundResults: state.roundResults,
        placement: state.placement,
        pendingTripleReward: state.pendingTripleReward,
        discoverOptions: state.discoverOptions,
        pendingAbilityDiscover: state.pendingAbilityDiscover,
        abilityDiscoverOptions: state.abilityDiscoverOptions,
        turnTimerMs: state.turnTimerMs,
        heroPowerUsedThisTurn: state.heroPowerUsedThisTurn,
      }),
      // After rehydration, reset mid-action transient state so the player
      // lands on a clean tavern view. Also sync the v1 singleton from the
      // restored human snapshot so the next combat sees real data.
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.turnTimerActive = false;
        state.heroPowerState = 'idle';
        state.heroPowerTargetType = null;
        state.heroPowerDiscoverOptions = [];
        if (state.heroId && state.phase !== 'idle') {
          const hero = allHeroes.find((h) => h.id === state.heroId);
          if (hero && v1GameState.players.length > 0) {
            v1GameState.players[0].board = [...state.board] as any;
            v1GameState.players[0].hand = [...state.hand] as any;
            v1GameState.players[0].health = state.health;
            v1GameState.players[0].gold = state.gold;
            v1GameState.players[0].tavernTier = state.tavernTier;
            v1GameState.players[0].hero = hero;
            v1GameState.players[0].heroPowerUsedThisTurn = state.heroPowerUsedThisTurn;
          }
        }
      },
      // Schema-version mismatch → drop persisted state (return undefined so
      // create()'s initial values apply). Safer than trying to migrate a
      // 30-field shape we might get wrong.
      migrate: (persisted, version) => {
        if (version !== MATCH_SCHEMA_VERSION) {
          return undefined as unknown as MatchState & MatchActions;
        }
        return persisted as MatchState & MatchActions;
      },
    },
  ),
);
